import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getAnomalies } from '../../../services/timesheetService';
import formatMinutes from '../../../utils/formatMinutes';
import timesheetStatusColors from '../../../utils/timesheetStatusColors';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { ANOMALY_CODES, anomalyLabel } from '../../../utils/anomalyLabels';
import PersonCell from '../../../components/ui/PersonCell';
import AnomalyBadges from '../../../components/ui/AnomalyBadges';
import { useTaFilterColumns } from '../../pdks-reports/config/taFilterColumns';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

// FirstEntry/LastExit backend'de DateTime? (ISO string). Duvar-saati göstermek için ISO string'in
// 'T' sonrası HH:mm kısmı DOĞRUDAN alınır — new Date(...).getHours() kullanmak tarayıcı saat
// dilimine göre saati KAYDIRIRDI (punch duvar-saati olduğundan yanlış olur).
const formatClock = (val) => {
  if (!val) return '-';
  const s = String(val);
  const tIdx = s.indexOf('T');
  if (tIdx >= 0 && s.length >= tIdx + 6) return s.substring(tIdx + 1, tIdx + 6);
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const useMissingRecordsApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getAnomalies(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  // Tek durum haritası (utils/timesheetStatusLabels) — partialLeave dahil hiçbir durum ham görünmez.
  const statusLabels = useMemo(() => translateTimesheetStatuses(t), [t]);
  // AKM POC §7.5 — ortak PDKS süzgeç seti (statü, grup, departman, lokasyon, vardiya, kişi); tabloda görünmez.
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('timesheet.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    dateFilterColumn('date', t('timesheet.date'), i18n.language),
    {
      // status: yalnız görüntü — anomalies ucu status filtresi almaz (yalnız code[eq]).
      name: 'status', label: t('timesheet.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const colors = timesheetStatusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    {
      // Kolon adı 'code' → dropdown filtre code[eq] üretir (parseTableParams kolon adını kullanır);
      // hücre ise satırın anomalies[] dizisini AnomalyBadges ile render eder (13 kod, anomalyLabel).
      name: 'code', label: t('timesheet.anomalies'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: ANOMALY_CODES,
          defaultOp: 'eq',
          renderValue: (v) => anomalyLabel(t, v),
        },
        customFilterListOptions: { render: (v) => `${t('timesheet.anomalies')}: ${anomalyLabel(t, v)}` },
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <AnomalyBadges codes={row?.anomalies} />;
        },
      },
    },
    {
      name: 'actualMinutes', label: t('timesheet.actual'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'outsideMinutes', label: t('timesheet.outside'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'firstEntry', label: t('pdks.firstEntry'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'lastExit', label: t('pdks.lastExit'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'sessionCount', label: t('timesheet.sessionCount'),
      options: { filter: false, sort: false, customBodyRender: (v) => v ?? 0 },
    },
    ...filterColumns,
  ], [t, i18n.language, data, statusLabels, filterColumns]);

  return { columns, data, fetching, pagination, refresh, statusLabels };
};

export default useMissingRecordsApi;
